import { useEffect } from "react"
import { useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "urql"
import { toast } from "sonner"
import { usePostHog } from "@posthog/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2 } from "lucide-react"
import { FieldError } from "@/components/shared/field-error"
import { applicationSchema, type ApplicationFormValues } from "@/lib/schemas"
import { CreateApplicationMutation, UpdateApplicationMutation } from "@/lib/graphql/outbound"
import type { ResultOf } from "@/lib/graphql/types"
import type { ApplicationsQuery } from "@/lib/graphql/outbound"

type AppItem = ResultOf<typeof ApplicationsQuery>["applications"]["nodes"][number]

interface ApplicationFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  initialData?: AppItem
}

export function ApplicationForm({ open, onOpenChange, onSuccess, initialData }: ApplicationFormProps) {
  const posthog = usePostHog()
  const isEdit = !!initialData
  const [, create] = useMutation(CreateApplicationMutation)
  const [, update] = useMutation(UpdateApplicationMutation)

  const form = useForm<ApplicationFormValues>({
    resolver: zodResolver(applicationSchema) as Resolver<ApplicationFormValues>,
    defaultValues: {
      name: initialData?.name ?? "",
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({ name: initialData?.name ?? "" })
    }
  }, [open, initialData, form])

  const onSubmit = async (values: ApplicationFormValues) => {
    if (isEdit) {
      const result = await update({ id: initialData.id, input: { name: values.name } })
      if (result.error) { toast.error(result.error.message); return }
      toast.success("Application updated")
    } else {
      const result = await create({ input: { name: values.name } })
      if (result.error) { toast.error(result.error.message); return }
      posthog?.capture("application_created")
      toast.success("Application created")
    }

    onSuccess()
    onOpenChange(false)
    form.reset()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Application" : "Create Application"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-4 rounded-lg glass-card p-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" {...form.register("name")} />
              <FieldError error={form.formState.errors.name} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
